import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import StickyBanner from './Notifications/StickyBanner';

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowPasswordChange?: boolean;
  requiredRole?: string | string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  allowPasswordChange = false,
  requiredRole
}) => {
  const { isAuthenticated, requirePasswordChange, user } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requirePasswordChange && !allowPasswordChange && location.pathname !== '/change-password') {
    // Redirect to change password if required and not already there
    return <Navigate to="/change-password" replace />;
  }

  if (!requirePasswordChange && location.pathname === '/change-password' && !allowPasswordChange) {
    // Redirect to home if password change not required but trying to access change password page
    return <Navigate to="/" replace />;
  }

  // Check role-based access
  if (requiredRole && (!user || (
    Array.isArray(requiredRole) 
      ? !requiredRole.includes(user.role)
      : user.role !== requiredRole
  ))) {
    console.log('Access denied - Required role:', requiredRole, 'User role:', user?.role);
    return <Navigate to="/" replace />;
  }

  // Redirect counsellors to their dashboard if they try to access the home page
  if (user?.role === 'counsellor' && location.pathname === '/') {
    return <Navigate to="/counsellor/dashboard" replace />;
  }

  // StickyBanner renders persistent high-priority notifications (credential
  // expiry warnings, suspension notices) at the top of every authenticated
  // page — it returns null when there is nothing to show.
  return (
    <>
      <StickyBanner />
      {children}
    </>
  );
};

export default ProtectedRoute;
